import React, { useState, useEffect } from "react";
import apiClient from "../../utils/apiClient";
import CoursesGrid from "./CoursesGrid";
import { Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import CommonToaster from "../Common/CommonToaster";
import OverLimitBanner from "../Common/OverLimitBanner";
import { ConfirmModal, AlertModal, AppButton } from "../ui";
import { usePlanUsage, isOverLimit, isStrictlyOverLimit } from "../../hooks/usePlanUsage";
import { BookOpen, Plus, LayoutGrid, List, Search } from "lucide-react";
import PageHeader from "../ui/PageHeader";
import "./Courses.scss";

const CoursesPage = () => {
  const [filters, setFilters] = useState({ search: "", instructor: "", batch: "" });
  const [courses, setCourses] = useState([]);
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '' });
  const navigate = useNavigate();

  // Plan usage limits
  const { usage } = usePlanUsage();
  const courseLimit = isOverLimit(usage?.courses);
  const courseStrictly = isStrictlyOverLimit(usage?.courses);

  useEffect(() => {
    const fetchCoursesAndOptions = async () => {
      setLoading(true);
      setErr("");
      try {
        const params = { courseType: 'batch' };
        if (filters.search) params.search = filters.search;
        if (filters.batch) params.batch = filters.batch;

        const { data } = await apiClient.get("/courses/list?q=info", { params });
        setCourses(data.courses || []);
      } catch (error) {
        setErr("Failed to load courses. Please try again.");
        setToast({ show: true, message: "Failed to load courses.", type: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchCoursesAndOptions();
  }, [filters]);

  const handleDelete = (id) => setDeleteTarget(id);

  const confirmDelete = async () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    try {
      await apiClient.delete(`/courses/delete/${id}`);
      setCourses(prev => prev.filter(c => c._id !== id));
      setToast({ show: true, message: "Course deleted successfully!", type: "success" });
    } catch (error) {
      if (error.response?.data?.errorCode === 'COURSE_HAS_BATCHES') {
        setAlertModal({ show: true, title: 'Cannot Delete Course', message: error.response.data.message });
      } else {
        setToast({ show: true, message: "Failed to delete course.", type: "error" });
      }
    }
  };

  return (
    <div className="cr-page">
      <PageHeader
        icon={BookOpen}
        title="Courses"
        description="Manage batch-based courses and their content"
        actions={
          <AppButton
            onClick={() => navigate("/courses/add")}
            disabled={courseLimit.overLimit}
            title={courseLimit.overLimit ? `Plan limit reached (${courseLimit.used}/${courseLimit.max})` : ''}
          >
            <Plus size={16} /> Add Course
          </AppButton>
        }
      />
      <OverLimitBanner
        resourceName="Courses"
        used={courseLimit.used}
        max={courseLimit.max}
        overLimit={courseLimit.overLimit}
        strictlyOver={courseStrictly.overLimit}
      />

      {/* Toolbar: Search + View Toggle */}
      <div className="cr-toolbar">
        <div className="cr-search">
          <Search size={16} className="cr-search-icon" />
          <input
            type="text"
            className="cr-search-input"
            placeholder="Search courses..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>
      </div>

      {loading && (
        <div className="cr-empty">
          <Spinner animation="border" variant="secondary" />
        </div>
      )}
      {err && (
        <Alert variant="danger" className="my-3 text-center">
          {err}
        </Alert>
      )}
      {!loading && !err && <CoursesGrid courses={courses} onDelete={handleDelete} />}
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Course"
        message="Are you sure you want to delete this course?"
        confirmText="Delete"
        variant="danger"
      />

      <AlertModal
        show={alertModal.show}
        onClose={() => setAlertModal({ show: false, title: '', message: '' })}
        title={alertModal.title}
        message={alertModal.message}
        variant="danger"
      />
    </div>
  );
};

export default CoursesPage;
